import React, { useState, useRef, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { showSuccess, showError } from "toast/toast";
import { useNavigate, useParams } from "react-router-dom";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import { addressLine1Validation, cityValidation, countryValidation, firstNameValidation, lastNameValidation, phoneValidation, stateValidation, zipcodeValidation } from "validationSchemas/common";
import { getAuthCookie } from "../../utils/authCookies";

const libraries: Array<"places"> = ["places"];
const apiKey: string = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const SignageAgentAddAddress = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const [preview, setPreview] = useState<string | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0); // Track upload progress
  const navigate = useNavigate();
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const [loading, setLoading] = useState(true);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
  });

  const [address, setAddress] = useState("");
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const formik = useFormik({
    initialValues: {
      label_of_address: "",
      firstName: "",
      lastName: "",
      company: "",
      addressLine1: "",
      addressLine2: "",
      country: "",
      county: "",
      city: "",
      state: "",
      zipcode: "",
      phone: "",
      cityCode: "",
      stateCode: "",
      countryCode: "",
      latitude: 0,
      longitude: 0,
    },
    validationSchema: Yup.object({
      label_of_address: Yup.string().required("Label of address is required"),
      firstName: firstNameValidation,
      lastName: lastNameValidation,
      addressLine1: addressLine1Validation,
      city: cityValidation,
      state: stateValidation,
      zipcode: zipcodeValidation,
      country: countryValidation,
      phone: phoneValidation
    }),
    onSubmit: async (values) => {
      setFormSubmitting(true);

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/addresses/user/${agentId}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            user_id: userId,
            label: values.label_of_address,
            first_name: values.firstName,
            last_name: values.lastName,
            company: values.company,
            address_details: {
              label: values.label_of_address,
              addressLine1: values.addressLine1,
              addressLine2: values.addressLine2,
              city: values.county,
              state: values.state,
              zip: values.zipcode,
              country: values.country,
              phone: values.phone,
              cityCode: values.cityCode,
              stateCode: values.stateCode,
              countryCode: values.countryCode,
              latitude: values.latitude,
              longitude: values.longitude,
            },
          }),
        });

        const result: any = await response.json();
        if (!response.ok || result?.error) {
          const message = result?.error || result?.message || "Failed to save data";
          console.error("API Error:", message);
          showError(message);
          throw new Error(message);
        }


        navigate(-1);
        showSuccess("Address added successfully!");
      } catch (error: any) {
        console.error(error);
      } finally {
        setFormSubmitting(false);
        setUploadProgress(0);
      }
    }
  });

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (!place || !place.address_components) {
        showError("Please select a valid address from the suggestions.");
        return;
      }

      const addressComponents = place.address_components;

      if (addressComponents) {
        const getComponent = (type: string) => {
          const component = addressComponents?.find(c => c.types.includes(type));
          return component ? { long_name: component.long_name, short_name: component.short_name } : { long_name: '', short_name: '' };
        };

        const streetNumber = getComponent('street_number').long_name;
        const route = getComponent('route').long_name;

        const cityObj = getComponent('locality') || getComponent('administrative_area_level_2');
        const stateObj = getComponent('administrative_area_level_1');
        const postalCode = getComponent('postal_code').long_name;
        const countryObj = getComponent('country');


        const street = `${streetNumber} ${route}`.trim();

        const latitude = place.geometry?.location?.lat();
        const longitude = place.geometry?.location?.lng();
        formik.setFieldValue('addressLine1', street);
        formik.setFieldValue('city', cityObj.long_name);
        formik.setFieldValue('county', cityObj.long_name);
        formik.setFieldValue('cityCode', cityObj.short_name); // <-- Add this
        formik.setFieldValue('state', stateObj.long_name);
        formik.setFieldValue('stateCode', stateObj.short_name); // <-- Add this
        formik.setFieldValue('zipcode', postalCode);
        formik.setFieldValue('country', countryObj.long_name);
        formik.setFieldValue('countryCode', countryObj.short_name);

        if (latitude && longitude) {
          formik.setFieldValue('latitude', latitude);
          formik.setFieldValue('longitude', longitude);
        }
        // :white_tick: ZIP code logic with fallback
        if (postalCode) {
          formik.setFieldValue("zipcode", postalCode);
        } else if (latitude && longitude) {
          // Fallback to reverse geocoding
          fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
          )
            .then((res) => res.json())
            .then((data) => {
              const postalComp = data.results[0]?.address_components.find((c: any) =>
                c.types.includes("postal_code")
              );
              if (postalComp) {
                console.log("Postal Code from Reverse Geocoding:", postalComp.long_name);
                formik.setFieldValue("zipcode", postalComp.long_name);
              } else {
                console.warn("No postal_code found even from reverse geocoding");
              }
            })
            .catch((err) => console.error("Reverse geocoding error:", err));
        }
      }
    }
  };

  return (
    <div className="content">
      {/* <div className="card2">
          <div className="card-body2"> */}
      <div className="col-md-12 d-flex justify-content-center">
        <div className=" col-md-9 ms-5 " style={{ marginTop: "0px" }}>
          <h3 className=" pt-1 fw-bold addheaders" >Add Address</h3>

          {/* form content here */}
        </div>
      </div>
      {/* <h2 className="d-flex justify-content-center">Add Address</h2> */}
      <div className="row ">
        <div className="col-md-12 d-flex justify-content-center">
          <div className="col-md-9">
            <div className="addheaders-content  card-bg-fill">
              <div className="p-2 px-4 mt-1">
                <form onSubmit={formik.handleSubmit}>
                  <div className=" row cust-margin-4">
                    <div>
                      <h5>Address Details</h5>
                      <hr className="lines" style={{ width: "110px" }} />
                    </div>
                    <div className="col-md-6 " >
                      <label className="form-label">
                        Label of address  <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="label_of_address"
                        name="label_of_address"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.label_of_address}
                      />
                      {formik.touched.label_of_address && formik.errors.label_of_address ? (
                        <div className="text-danger">
                          {formik.errors.label_of_address}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 ">
                      <label className="form-label">
                        First Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="firstName"
                        name="firstName"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.firstName}
                      />
                      {formik.touched.firstName && formik.errors.firstName ? (
                        <div className="text-danger">
                          {formik.errors.firstName}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 ">
                      <label className="form-label">
                        Last Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="lastName"
                        name="lastName"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.lastName}
                      />
                      {formik.touched.lastName && formik.errors.lastName ? (
                        <div className="text-danger">
                          {formik.errors.lastName}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 ">
                      <label className="form-label">
                        Company
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="company"
                        name="company"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.company}
                      />
                      {formik.touched.company && formik.errors.company ? (
                        <div className="text-danger">
                          {formik.errors.company}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 ">
                      <label className="form-label">
                        Phone number  <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="phone"
                        name="phone"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.phone}
                      />
                      {formik.touched.phone && formik.errors.phone ? (
                        <div className="text-danger">
                          {formik.errors.phone}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 ">
                      <label className="form-label">
                        Address Line 1 <span className="text-danger">*</span>
                      </label>
                      {isLoaded && (
                        <Autocomplete
                          onLoad={(autocomplete) => {
                            autocompleteRef.current = autocomplete;
                          }}
                          onPlaceChanged={handlePlaceChanged}
                        >
                          <input
                            type="text"
                            className="form-control"
                            id="addressLine1"
                            name="addressLine1"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.addressLine1}
                          />
                        </Autocomplete>
                      )}
                      {formik.touched.addressLine1 && formik.errors.addressLine1 ? (
                        <div className="text-danger">
                          {formik.errors.addressLine1}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 ">
                      <label className="form-label">
                        Address Line 2
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="addressLine2"
                        name="addressLine2"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.addressLine2}
                      />
                      {formik.touched.addressLine2 && formik.errors.addressLine2 ? (
                        <div className="invalid-feedback">
                          {formik.errors.addressLine2}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 ">
                      <label className="form-label">
                        City <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="city"
                        name="city"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.city}
                      />
                      {formik.touched.city && formik.errors.city ? (
                        <div className="text-danger">
                          {formik.errors.city}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 ">
                      <label className="form-label">
                        State <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="state"
                        name="state"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.state}
                      />
                      {formik.touched.state && formik.errors.state ? (
                        <div className="text-danger">
                          {formik.errors.state}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 ">
                      <label className="form-label">
                        Zip Code <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="zipcode"
                        name="zipcode"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.zipcode}
                      />
                      {formik.touched.zipcode && formik.errors.zipcode ? (
                        <div className="text-danger">
                          {formik.errors.zipcode}
                        </div>
                      ) : null}
                    </div> <div className="col-md-6 ">
                      <label className="form-label">
                        Country <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="country"
                        name="country"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.country}
                      />
                      {formik.touched.country && formik.errors.country ? (
                        <div className="text-danger">
                          {formik.errors.country}
                        </div>
                      ) : null}
                    </div>
                  </div>
                 <div className="row mb-3 rcust-margin-4">
                        <div className="col-md-12 mt-2 d-flex justify-content-between">

                      <button
                        className="btn btn-outline-secondary br-8 backbutton"
                        type="button"
                        onClick={() => navigate(-1)}
                      >
                        Back
                      </button>

                      <button className="btn btn-primary submit-btn " type="submit">
                        Submit
                      </button>
                    </div>
                  </div>

                </form>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default SignageAgentAddAddress;
